import {
  CLIENT_MESSAGES,
  SUPPORTED_LOCALES,
  type InteractMessage,
  type Locale,
} from '@veriverse/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLocale } from '../i18n.js';
import { api } from '../net/api.js';
import { leaveOffice } from '../net/connection.js';
import { totalUnread } from '../state/dm-logic.js';
import { useOnboardingStore } from '../state/onboarding.js';
import { useSessionStore } from '../state/session-store.js';
import { LazyAdminPanel } from './LazyAdminPanel.js';
import { PauseMenu } from './PauseMenu.js';
import { SettingsPanel } from './SettingsPanel.js';
import { ColleaguesBox } from './ColleaguesBox.js';
import { CalendarPanel } from './CalendarPanel.js';
import { EmoteWheel } from './EmoteWheel.js';
import { DancePanel } from './DancePanel.js';
import { PongPanel } from './PongPanel.js';
import { CatPuzzlePanel } from './CatPuzzlePanel.js';
import { useCatPuzzleStore } from '../state/cat-puzzle.js';
import { CoopDoorProgress } from './CoopDoorProgress.js';
import { DeskPanel } from './DeskPanel.js';
import { useDeskStore } from '../state/desk.js';
import { PinDoorPanel } from './PinDoorPanel.js';
import { usePinDoorStore } from '../state/pin-door.js';
import { InteractionPrompt } from './InteractionPrompt.js';
import { HudWeather } from './HudWeather.js';
import { OfficeBoards } from './OfficeBoards.js';
import { OfficeClock } from './OfficeClock.js';

/** Overlay on top of the canvas: colleagues, clock, weather and the Menu button (Esc). */
export function Hud() {
  const { t } = useTranslation();
  const isAdmin = useSessionStore((s) => s.user?.role === 'admin');
  const [adminOpen, setAdminOpen] = useState(false);
  const calendarOpen = useSessionStore((s) => s.calendarOpen);
  const setCalendarOpen = useSessionStore((s) => s.setCalendarOpen);
  const room = useSessionStore((s) => s.room);
  const gamePanel = useSessionStore((s) => s.gamePanel);
  const unread = useSessionStore((s) => totalUnread(s.conversations));
  const catPuzzle = useCatPuzzleStore((s) => s.objectId);
  const pinDoor = usePinDoorStore((s) => s.objectId);
  const deskPrompt = useDeskStore((s) => s.prompt);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Escape opens the menu (and closes it again). It stays out of the way while typing and while
  // any other panel or menu is open, since those use Escape to close themselves.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (menuOpen) {
        event.stopImmediatePropagation();
        setMenuOpen(false);
        return;
      }
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (typing || document.querySelector('[role="dialog"], [role="menu"], .overlay')) return;
      setMenuOpen(true);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [menuOpen]);

  return (
    <>
      <div className="hud">
        <button
          type="button"
          className="secondary hud-menu-button"
          aria-haspopup="dialog"
          onClick={() => setMenuOpen(true)}
        >
          {t('hud.menu')}
          {unread > 0 && <span className="badge">{unread}</span>}
        </button>
        <ColleaguesBox />
        <OfficeClock />
        <HudWeather />
      </div>
      <div className="beta-watermark" aria-hidden="true">
        {t('hud.betaWatermark', { version: __APP_VERSION__ })}
      </div>
      {menuOpen && (
        <PauseMenu
          unread={unread}
          isAdmin={isAdmin}
          onClose={() => setMenuOpen(false)}
          onMessages={() =>
            useSessionStore.getState().setDmOpen(!useSessionStore.getState().dmOpen)
          }
          onCalendar={() => setCalendarOpen(true)}
          onAdmin={() => setAdminOpen(true)}
          onSettings={() => setSettingsOpen(true)}
          onLeave={leaveOffice}
        />
      )}
      {adminOpen && (
        <div className="overlay">
          <LazyAdminPanel onClose={() => setAdminOpen(false)} />
        </div>
      )}
      <InteractionPrompt />
      {room && <CoopDoorProgress room={room} />}
      {room && <EmoteWheel room={room} />}
      <OfficeBoards />
      {room && gamePanel?.kind === 'dance' && (
        <DancePanel room={room} objectId={gamePanel.objectId} onClose={closeGame} />
      )}
      {room && gamePanel?.kind === 'pong' && (
        <PongPanel room={room} objectId={gamePanel.objectId} onClose={closeGame} />
      )}
      {calendarOpen && <CalendarPanel onClose={() => setCalendarOpen(false)} />}
      {catPuzzle !== undefined && <CatPuzzlePanel onClose={() => closeCatPuzzle(catPuzzle)} />}
      {pinDoor !== undefined && <PinDoorPanel objectId={pinDoor} />}
      {deskPrompt && <DeskPanel prompt={deskPrompt} />}
      {settingsOpen && (
        <SettingsPanel
          onClose={() => setSettingsOpen(false)}
          onReplayTutorial={() => {
            setSettingsOpen(false);
            useOnboardingStore.getState().startTutorial();
          }}
        />
      )}
    </>
  );
}

const closeGame = () => useSessionStore.getState().setGamePanel(undefined);

/** Switches the language and remembers it on the account too (best effort, when signed in). */
export function chooseLocale(locale: Locale): void {
  changeLocale(locale);
  if (useSessionStore.getState().auth === 'signed-in') {
    void api('/api/me/preferences', { method: 'PATCH', body: { locale } }).catch(() => undefined);
  }
}

export function LanguageSwitch({ current, label }: { current: string; label: string }) {
  const onChange = chooseLocale;
  return (
    <label className="hud-box language">
      <span>{label}</span>
      <select value={current} onChange={(event) => onChange(event.target.value as Locale)}>
        {SUPPORTED_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {locale === 'sv' ? 'Svenska' : 'English'}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Closes the puzzle and tells the cat, so the "playing" icon goes away (spec §6.14.4). */
function closeCatPuzzle(objectId: number) {
  useCatPuzzleStore.getState().close();
  const message: InteractMessage = { objectId, action: 'close' };
  useSessionStore.getState().room?.send(CLIENT_MESSAGES.interact, message);
}
